/**
 * Formatação monetária pt-BR a partir de strings de centavos (BigInt). Nunca float.
 * Espelha packages/domain/src/money.ts sem depender do build do pacote.
 */
export const E8 = 100_000_000n;

export type CentsLike = string | number | bigint | null | undefined;

export function toBig(v: CentsLike): bigint {
  if (v === null || v === undefined || v === "") return 0n;
  if (typeof v === "bigint") return v;
  if (typeof v === "number") {
    if (!Number.isInteger(v)) throw new Error("centavos devem ser inteiros");
    return BigInt(v);
  }
  if (!/^-?\d+$/.test(v)) throw new Error(`centavos inválidos: ${v}`);
  return BigInt(v);
}

function group(int: bigint): string {
  return int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** "5492628" -> "54.926,28" */
export function formatCents(v: CentsLike, opts: { symbol?: string; decimals?: boolean } = {}): string {
  const cents = toBig(v);
  const neg = cents < 0n;
  const abs = neg ? -cents : cents;
  const int = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, "0");
  const body = opts.decimals === false ? group(int) : `${group(int)},${frac}`;
  return `${neg ? "-" : ""}${opts.symbol ? opts.symbol + " " : ""}${body}`;
}

export function brl(v: CentsLike): string {
  return formatCents(v, { symbol: "R$" });
}
export function usd(v: CentsLike): string {
  return formatCents(v, { symbol: "USD" });
}
/** Valor inteiro sem centavos, para KPIs: "R$ 389.305" */
export function brlRound(v: CentsLike): string {
  const cents = toBig(v);
  const rounded = cents >= 0n ? (cents + 50n) / 100n : -((-cents + 50n) / 100n); // half-up para o real inteiro
  return formatCents(rounded * 100n, { symbol: "R$", decimals: false });
}

/** "542000000" -> "5,42000000" (8 casas) */
export function rate(v: CentsLike): string {
  const r = toBig(v);
  const int = r / E8;
  const frac = (r % E8).toString().padStart(8, "0");
  return `${int.toString()},${frac}`;
}

/** basis points -> "0,35%" */
export function pct(bp: number | string): string {
  const n = typeof bp === "string" ? Number(bp) : bp;
  const abs = Math.abs(n);
  const int = Math.floor(abs / 100);
  const frac = (abs % 100).toString().padStart(2, "0");
  return `${n < 0 ? "-" : ""}${int},${frac}%`;
}

/** Arredondamento half-up de divisão de inteiros. */
export function divRound(n: bigint, d: bigint): bigint {
  if (d === 0n) throw new Error("divisão por zero");
  const neg = (n < 0n) !== (d < 0n);
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = an / ad;
  const r = an % ad;
  const rounded = r * 2n >= ad ? q + 1n : q;
  return neg ? -rounded : rounded;
}

/** Câmbio efetivo (brl/usd) com 8 casas, como string escalada. */
export function effectiveRateE8(brlCents: CentsLike, usdCents: CentsLike): bigint {
  const u = toBig(usdCents);
  if (u <= 0n) return 0n;
  return divRound(toBig(brlCents) * E8, u);
}

/** Custo total em bp sobre a base: (total - base) / base. */
export function costBp(totalCents: CentsLike, baseCents: CentsLike): number {
  const base = toBig(baseCents);
  if (base <= 0n) return 0;
  return Number(divRound((toBig(totalCents) - base) * 10_000n, base));
}

/** Aceita "10.000,00", "10000.00", "10000", "1,5" -> centavos (bigint). */
export function parseDecimalToCents(input: string): bigint {
  let s = input.trim().replace(/[^\d,.-]/g, "");
  if (s === "" || s === "-") throw new Error("valor vazio");
  const neg = s.startsWith("-");
  if (neg) s = s.slice(1);
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if ((s.match(/\./g) ?? []).length > 1) s = s.replace(/\./g, "");
  const [intPart = "", fracRaw = ""] = s.split(".");
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracRaw)) throw new Error(`valor inválido: ${input}`);
  const frac = (fracRaw + "00").slice(0, 2);
  const extra = fracRaw.slice(2);
  let cents = BigInt(intPart || "0") * 100n + BigInt(frac || "0");
  if (extra && extra[0]! >= "5") cents += 1n;
  return neg ? -cents : cents;
}

/**
 * Máscara pt-BR para campo de valor: só dígitos, os dois últimos são centavos.
 * "1000000" -> "10.000,00". Vazio -> "".
 */
export function maskDecimalInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (digits === "") return "";
  const padded = digits.padStart(3, "0");
  const int = padded.slice(0, -2);
  const frac = padded.slice(-2);
  return `${group(BigInt(int))},${frac}`;
}

/** "10.000,00" (mascarado) -> "1000000" */
export function maskedToCents(masked: string): string {
  if (masked.trim() === "") return "0";
  return parseDecimalToCents(masked).toString();
}

export function sumCents(values: CentsLike[]): bigint {
  return values.reduce<bigint>((acc, v) => acc + toBig(v), 0n);
}

/** Segundos -> "m:ss" */
export function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}
